import React, { memo, useRef, useEffect, useCallback, useState } from 'react';
import type { CSSProperties } from 'react';
import type {
  RollerConfig,
  RollerWheel,
  RollerRangeOptions,
  RollerListOptions,
  TypographyStyle,
} from '@segmently/schema-models/v2';
import type { PreviewMode } from '../../../types/previewMode';
import { mapTypographyStyles, mapEdgeInsetsToCSS, mapCornerRadiusToCSS } from '../../../utils/styleMapper';
import { useFontOverrides } from '@modules/font-config/context/FontConfigContext';
import { getSchemaTestId } from '../../../utils/testIdUtils';
import styles from './Picker.module.css';

// ============================================================================
// Constants
// ============================================================================

const ITEM_HEIGHT = 40;
const VISIBLE_ITEMS = 5;
const FRICTION = 0.92;
const MIN_VELOCITY = 0.5;
const SNAP_DURATION = 300;
const PERSPECTIVE = 1000;
const MAX_ROTATION = 60;

// ============================================================================
// Helper Functions
// ============================================================================

function applyOpacityToColor(hexColor: string, opacity: number): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Extracts items from a roller wheel based on its kind.
 * V2: RollerWheel with RollerRangeOptions | RollerListOptions
 */
function getWheelItems(wheel: RollerWheel, locale: string): string[] {
  if (wheel.kind === 'range') {
    const { from, to } = wheel.options as RollerRangeOptions;
    const items: string[] = [];
    for (let i = from; i <= to; i++) {
      items.push(String(i));
    }
    return items;
  }
  // List type
  const localizedOptions = (wheel.options as RollerListOptions)?.localizedOptions;
  return localizedOptions?.[locale] || localizedOptions?.['en-US'] || [];
}

// ============================================================================
// Types
// ============================================================================

export interface PickerProps {
  roller: RollerConfig | undefined;
  locale: string;
  selectedIndex?: number;
  onIndexChange?: (index: number, value: string) => void;
  className?: string;
  'data-testid'?: string;
  editPath?: string;
  previewMode?: PreviewMode;
}

interface WheelState {
  offset: number;
  velocity: number;
  isDragging: boolean;
  dragStartY: number;
  dragStartOffset: number;
  lastMoveY: number;
  lastMoveTime: number;
}

// ============================================================================
// Single Wheel Component (Pure UI — no schema deps)
// ============================================================================

interface WheelProps {
  items: string[];
  selectedIndex: number;
  onSelect: (index: number, value: string) => void;
  labelAppearance?: TypographyStyle;
  disabled?: boolean;
}

const Wheel = memo(function Wheel({ items, selectedIndex, onSelect, labelAppearance, disabled }: WheelProps) {
  const fontOverrides = useFontOverrides();
  const containerRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const hasDraggedRef = useRef(false);
  const stateRef = useRef<WheelState>({
    offset: -selectedIndex * ITEM_HEIGHT,
    velocity: 0,
    isDragging: false,
    dragStartY: 0,
    dragStartOffset: 0,
    lastMoveY: 0,
    lastMoveTime: 0,
  });

  const [, forceUpdate] = useState(0);

  const centerOffset = Math.floor(VISIBLE_ITEMS / 2) * ITEM_HEIGHT;

  const clampOffset = useCallback((offset: number): number => {
    const maxOffset = 0;
    const minOffset = -(items.length - 1) * ITEM_HEIGHT;
    return Math.max(minOffset, Math.min(maxOffset, offset));
  }, [items.length]);

  const getIndexFromOffset = useCallback((offset: number): number => {
    const index = Math.round(-offset / ITEM_HEIGHT);
    return Math.max(0, Math.min(items.length - 1, index));
  }, [items.length]);

  const animateToOffset = useCallback((targetOffset: number, duration: number = SNAP_DURATION) => {
    const startOffset = stateRef.current.offset;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      stateRef.current.offset = startOffset + (targetOffset - startOffset) * eased;
      forceUpdate(n => n + 1);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        stateRef.current.offset = targetOffset;
        const newIndex = getIndexFromOffset(targetOffset);
        onSelect(newIndex, items[newIndex]);
      }
    };

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }
    rafRef.current = requestAnimationFrame(animate);
  }, [getIndexFromOffset, items, onSelect]);

  const applyInertia = useCallback(() => {
    const state = stateRef.current;

    if (Math.abs(state.velocity) < MIN_VELOCITY) {
      const targetOffset = -getIndexFromOffset(state.offset) * ITEM_HEIGHT;
      animateToOffset(clampOffset(targetOffset));
      return;
    }

    state.velocity *= FRICTION;
    state.offset = clampOffset(state.offset + state.velocity);
    forceUpdate(n => n + 1);

    const clampedOffset = clampOffset(state.offset);
    if (clampedOffset !== state.offset) {
      state.offset = clampedOffset;
      state.velocity *= 0.5;
    }

    rafRef.current = requestAnimationFrame(applyInertia);
  }, [animateToOffset, clampOffset, getIndexFromOffset]);

  const handleDragStart = useCallback((clientY: number) => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    hasDraggedRef.current = false;
    const state = stateRef.current;
    state.isDragging = true;
    state.dragStartY = clientY;
    state.dragStartOffset = state.offset;
    state.lastMoveY = clientY;
    state.lastMoveTime = performance.now();
    state.velocity = 0;
  }, []);

  const handleDragMove = useCallback((clientY: number) => {
    const state = stateRef.current;
    if (!state.isDragging) return;

    const now = performance.now();
    const deltaY = clientY - state.lastMoveY;
    const deltaTime = now - state.lastMoveTime;

    if (Math.abs(clientY - state.dragStartY) > 5) {
      hasDraggedRef.current = true;
    }

    if (deltaTime > 0) {
      state.velocity = (deltaY / deltaTime) * 16.67;
    }

    state.offset = state.dragStartOffset + (clientY - state.dragStartY);
    state.lastMoveY = clientY;
    state.lastMoveTime = now;

    forceUpdate(n => n + 1);
  }, []);

  const handleDragEnd = useCallback(() => {
    const state = stateRef.current;
    if (!state.isDragging) return;

    state.isDragging = false;

    if (Math.abs(state.velocity) > MIN_VELOCITY) {
      rafRef.current = requestAnimationFrame(applyInertia);
    } else {
      const targetOffset = -getIndexFromOffset(state.offset) * ITEM_HEIGHT;
      animateToOffset(clampOffset(targetOffset));
    }
  }, [animateToOffset, applyInertia, clampOffset, getIndexFromOffset]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientY);

    const handleMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientY);
    };

    const handleMouseUp = () => {
      handleDragEnd();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [handleDragEnd, handleDragMove, handleDragStart]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientY);
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleDragMove(touch.clientY);
  }, [handleDragMove]);

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  useEffect(() => {
    const targetOffset = -selectedIndex * ITEM_HEIGHT;
    if (!stateRef.current.isDragging && Math.abs(stateRef.current.offset - targetOffset) > 1) {
      animateToOffset(targetOffset);
    }
  }, [selectedIndex, animateToOffset]);

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const handleItemClick = useCallback((index: number) => {
    if (!hasDraggedRef.current) {
      const targetOffset = -index * ITEM_HEIGHT;
      onSelect(index, items[index]);
      animateToOffset(clampOffset(targetOffset));
    }
  }, [animateToOffset, clampOffset, items, onSelect]);

  const renderItems = () => {
    const currentOffset = stateRef.current.offset;
    // V2: mapTypographyStyles instead of mapLabelBlockStyles
    const baseStyles = mapTypographyStyles(labelAppearance, fontOverrides);

    return items.map((item, index) => {
      const visualOffset = index * ITEM_HEIGHT + currentOffset + centerOffset;
      const distanceFromCenter = index * ITEM_HEIGHT + currentOffset;
      const normalizedDistance = distanceFromCenter / (ITEM_HEIGHT * 2);
      const rotation = Math.max(-MAX_ROTATION, Math.min(MAX_ROTATION, normalizedDistance * 25));
      const absDistance = Math.abs(distanceFromCenter);
      const opacity = Math.max(0.2, 1 - (absDistance / (ITEM_HEIGHT * 3)));
      const scale = Math.max(0.8, 1 - Math.abs(normalizedDistance) * 0.1);
      const zPosition = -Math.abs(distanceFromCenter) * 0.3;
      const isSelected = Math.abs(distanceFromCenter) < ITEM_HEIGHT / 2;

      const style: CSSProperties = {
        ...baseStyles,
        transform: `
          translateY(${visualOffset}px)
          translateZ(${zPosition}px)
          rotateX(${-rotation}deg)
          scale(${scale})
        `,
        opacity,
        fontWeight: labelAppearance?.fontWeight ?? (isSelected ? 600 : 400),
        color: isSelected
          ? (labelAppearance?.color || '#000')
          : (labelAppearance?.color ? applyOpacityToColor(labelAppearance.color, 0.75) : '#666'),
      };

      return (
        <div
          key={index}
          className={styles.wheelItem}
          style={style}
          data-testid={`picker-item-${index}`}
          data-selected={isSelected ? 'true' : 'false'}
          onClick={disabled ? undefined : () => handleItemClick(index)}
        >
          {item}
        </div>
      );
    });
  };

  const wheelHeight = VISIBLE_ITEMS * ITEM_HEIGHT;

  return (
    <div
      ref={containerRef}
      className={styles.wheel}
      style={{ height: wheelHeight, perspective: PERSPECTIVE, cursor: disabled ? 'default' : 'grab' }}
      onMouseDown={disabled ? undefined : handleMouseDown}
      onTouchStart={disabled ? undefined : handleTouchStart}
      onTouchMove={disabled ? undefined : handleTouchMove}
      onTouchEnd={disabled ? undefined : handleTouchEnd}
    >
      <div className={styles.wheelInner} style={{ transformStyle: 'preserve-3d' }}>
        {renderItems()}
      </div>

      <div
        className={styles.selectionIndicator}
        style={{
          top: centerOffset,
          height: ITEM_HEIGHT,
        }}
      />
    </div>
  );
});

// ============================================================================
// Main Picker Component
// ============================================================================

export const Picker = memo(function Picker({
  roller,
  locale,
  selectedIndex = 0,
  onIndexChange,
  className,
  'data-testid': testId,
  editPath,
  previewMode = 'live',
}: PickerProps): React.ReactElement | null {
  const isInteractive = previewMode === 'live' || previewMode === 'edit';

  if (!roller) {
    return null;
  }

  const { wheels, appearance, separator, labelAppearance, defaultValue } = roller;

  // Track selected indices for each wheel
  const [wheelIndices, setWheelIndices] = useState<number[]>(() => {
    return wheels?.map((wheel, idx) => {
      if (idx === 0) {
        if (selectedIndex > 0) {
          return selectedIndex;
        }
        const rootDefaultVal = defaultValue?.[locale] || defaultValue?.['en-US'];
        if (rootDefaultVal) {
          const items = getWheelItems(wheel, locale);
          const defaultIdx = items.indexOf(rootDefaultVal);
          if (defaultIdx >= 0) {
            return defaultIdx;
          }
        }
        return selectedIndex;
      }
      const defaultVal = wheel.defaultValue?.[locale] || wheel.defaultValue?.['en-US'];
      const items = getWheelItems(wheel, locale);

      if (defaultVal && items.length > 0) {
        const defaultIdx = items.indexOf(defaultVal);
        return defaultIdx >= 0 ? defaultIdx : 0;
      }
      return 0;
    }) || [selectedIndex];
  });

  // Sync wheelIndices when wheel.defaultValue changes
  useEffect(() => {
    if (!wheels?.length) return;

    const newIndices = wheels.map((wheel, idx) => {
      const defaultVal = wheel.defaultValue?.[locale] || wheel.defaultValue?.['en-US'];
      if (defaultVal) {
        const items = getWheelItems(wheel, locale);
        const defaultIdx = items.indexOf(defaultVal);
        if (defaultIdx >= 0) {
          return defaultIdx;
        }
      }
      return wheelIndices[idx] ?? 0;
    });

    const hasChanges = newIndices.some((val, idx) => val !== wheelIndices[idx]);
    if (hasChanges) {
      setWheelIndices(newIndices);
    }
  }, [wheels, locale]);

  const handleWheelSelect = useCallback((wheelIndex: number, itemIndex: number, _value: string) => {
    setWheelIndices(prev => {
      const newIndices = [...prev];
      newIndices[wheelIndex] = itemIndex;
      return newIndices;
    });

    if (onIndexChange) {
      const allValues = wheels?.map((wheel, idx) => {
        const items = getWheelItems(wheel, locale);
        const selectedIdx = idx === wheelIndex ? itemIndex : wheelIndices[idx];
        return items[selectedIdx] || '';
      }) || [];

      const combinedValue = allValues.join(separator || '');
      onIndexChange(itemIndex, combinedValue);
    }
  }, [locale, onIndexChange, separator, wheelIndices, wheels]);

  // V2: Typed RollerStyle with BorderStyle + EdgeInsets
  const containerStyle: CSSProperties = {
    // The editor exposes a "background color" control writing roller.appearance.backgroundColor
    // (v2RollerStyleConfig.ts:86); read it here so the control is not a silent no-op.
    ...(appearance?.backgroundColor && { backgroundColor: appearance.backgroundColor }),
    ...mapEdgeInsetsToCSS(appearance?.insets),
    ...mapCornerRadiusToCSS(appearance?.borders?.radius),
    ...(appearance?.borders?.width !== undefined && {
      borderWidth: appearance.borders.width,
      borderStyle: 'solid',
    }),
    ...(appearance?.borders?.color && { borderColor: appearance.borders.color }),
  };

  const schemaTestId = testId ?? getSchemaTestId(editPath);

  return (
    <div
      className={`${styles.picker}${className ? ` ${className}` : ''}`}
      style={containerStyle}
      data-testid={schemaTestId}
      data-edit-path={editPath}
    >
      {wheels?.map((wheel, wheelIndex) => {
        const items = getWheelItems(wheel, locale);

        if (items.length === 0) return null;

        return (
          <React.Fragment key={wheelIndex}>
            {wheelIndex > 0 && separator && (
              <div className={styles.separator}>{separator}</div>
            )}

            <Wheel
              items={items}
              selectedIndex={wheelIndices[wheelIndex] ?? 0}
              onSelect={(index, value) => handleWheelSelect(wheelIndex, index, value)}
              labelAppearance={labelAppearance}
              disabled={!isInteractive}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
});

Picker.displayName = 'Picker';
